import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ornament } from "../components/Ornament";
import { getStoryWeekWelcome } from "../data/storyWeek";
import { RecordingTips } from "../components/RecordingTips";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export default function StoryWeekWelcome() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [resendState, setResendState] = useState("idle");
  const W = getStoryWeekWelcome();

  useEffect(() => {
    if (loading) return;
    if (!user) return; // public welcome (unauth users see intro + CTA)
    // Authed: load their session if any so we can resume correctly
    (async () => {
      try {
        const s = await api.get("/story_week/mine");
        setSession(s.data);
      } catch (e) { /* ignore — first-time users have no session yet */ }
    })();
  }, [user, loading]);

  const nextDay = () => {
    if (!session) return 1;
    const days = session.days || {};
    const skipped = new Set(session.skipped_days || []);
    const avail = session.available_day || 1;
    for (let i = 1; i <= 7; i++) {
      if (!days[String(i)] && !skipped.has(i)) return Math.min(i, avail);
    }
    return 8; // all done
  };

  const begin = () => {
    setError("");
    // Unauthed users: force free-account registration first
    if (!user) {
      navigate(`/login?flow=story_week&next=${encodeURIComponent("/story-week")}`);
      return;
    }
    // Authed but not verified: nudge to setup page which shows the resend UI
    if (!user.email_verified) {
      navigate("/story-week/setup");
      return;
    }
    // Authed & verified: setup if no story_mode yet, otherwise jump to next day
    if (!session || !session.story_mode) {
      navigate("/story-week/setup");
      return;
    }
    const d = nextDay();
    if (d > 7) navigate("/story-week/complete");
    else navigate(`/story-week/day/${d}`);
  };

  const resend = async () => {
    setResendState("sending");
    try {
      await api.post("/auth/resend-verification", { origin_url: window.location.origin, flow: "story_week" });
      setResendState("sent");
    } catch (e) {
      setResendState("err");
    }
  };

  const doneCount = session ? Object.keys(session.days || {}).length : 0;
  const availDay = session?.available_day || 1;

  return (
    <div className="min-h-screen paper" data-testid="story-week-welcome">
      <header className="max-w-5xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-[#B8973A] text-lg" style={{ fontFamily: "Newsreader, serif" }}>✦</span>
          <span style={{ fontFamily: "Newsreader, serif", letterSpacing: "0.04em" }}>The Living Book</span>
        </div>
        {!user && (
          <button onClick={() => navigate("/login")} className="label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors" style={{ minHeight: 44 }} data-testid="sw-signin-link">
            Sign in
          </button>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-12 sm:py-20 text-center">
        <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.35em]">STORY WEEK</p>
        <h1 className="mt-8 text-5xl sm:text-7xl" style={{ fontFamily: "Newsreader, serif", fontWeight: 300, lineHeight: 1.05 }}>
          {W.title}
        </h1>
        <Ornament className="my-10" />
        <p className="text-2xl sm:text-3xl text-[#1B2A4A]" style={{ fontFamily: "Newsreader, serif", fontStyle: "italic", lineHeight: 1.35 }}>
          {W.subtitle}
        </p>
        <div className="mt-14 max-w-xl mx-auto space-y-6 text-left text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "17px", lineHeight: 1.75 }}>
          {W.body.split("\n\n").map((p, i) => (<p key={i}>{p}</p>))}
        </div>

        {/* Verification banner for authed-but-unverified users */}
        {user && !user.email_verified && (
          <div className="mt-10 p-5 text-left max-w-xl mx-auto" style={{ background: "#F7F0E0", borderLeft: "3px solid #B8973A", borderRadius: 2 }} data-testid="sw-verify-banner">
            <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.25em] mb-2">CONFIRM YOUR EMAIL</p>
            <p className="text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "16px", lineHeight: 1.65 }}>
              We've sent a confirmation link to <strong>{user.email}</strong>. Day 1 opens as soon as you click it.
            </p>
            <button type="button" onClick={resend} disabled={resendState === "sending"} className="mt-3 label-ui text-[11px] text-[#B8973A] hover:text-[#111D33] underline underline-offset-4" data-testid="sw-resend-btn">
              {resendState === "sent" ? "Sent — check your inbox" : resendState === "sending" ? "Sending…" : "Resend the confirmation link"}
            </button>
          </div>
        )}

        {/* Progress indicator */}
        {user && user.email_verified && session && (session.days && Object.keys(session.days).length > 0) && (
          <div className="mt-10 text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif" }} data-testid="sw-progress">
            <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.25em] mb-2">YOUR PROGRESS</p>
            <p>{doneCount} of 7 begun · Day {availDay} available now</p>
            {session.next_unlock_at && availDay < 7 && (
              <p className="mt-1 text-sm text-[#8C8278]">
                Day {availDay + 1} opens at {new Date(session.next_unlock_at).toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" })}.
              </p>
            )}
          </div>
        )}

        <div className="mt-14">
          <button
            onClick={begin}
            disabled={busy}
            className="btn-gold text-lg px-12 py-4"
            style={{ minHeight: 52 }}
            data-testid="sw-begin-btn"
          >
            {busy ? "Beginning…"
              : !user ? "Begin Story Week — Free"
              : !user.email_verified ? "Confirm your email to begin"
              : doneCount > 0 ? `Continue — Day ${Math.min(nextDay(), 7)}`
              : "Begin Day 1"}
          </button>
          {!user && (
            <p className="mt-4 text-[#8C8278]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px" }}>
              Free account required. No card needed.
            </p>
          )}
          {user && doneCount > 0 && (
            <p className="mt-4">
              <button
                type="button"
                onClick={() => navigate("/my-living-book")}
                className="label-ui text-[12px] text-[#B8973A] hover:text-[#111D33] underline underline-offset-4"
                style={{ minHeight: 44, fontFamily: "'DM Sans', sans-serif" }}
                data-testid="sw-welcome-view-mlb"
              >
                View My Living Book
              </button>
            </p>
          )}
        </div>
        {error && <p className="mt-4 text-red-700 text-sm">{error}</p>}

        {/* Recording tips — always visible on Welcome */}
        <div className="mt-14 max-w-xl mx-auto text-left">
          <RecordingTips variant="panel" />
        </div>

        <p className="mt-12 text-[#8C8278] italic" style={{ fontFamily: "Newsreader, serif", fontSize: "1.05rem" }}>
          We don't preserve stories. We begin conversations.
        </p>
      </main>
    </div>
  );
}
